import type { CanvasLayoutBox } from '@/lib/transforms/types/canvas-layout-box'
import { screenPxToCanvasPx } from '@/lib/text/text-tool-create'

/** Minimum on-screen thickness (per axis) so extreme aspect-ratio shapes stay visible. */
export const MIN_SHAPE_AXIS_VISUAL_SCREEN_PX = 1

function axisNeedsMinVisualScreenSize(
  axisCanvasSize: number,
  otherAxisCanvasSize: number,
  canvasZoom: number
): boolean {
  if (!Number.isFinite(axisCanvasSize) || !Number.isFinite(otherAxisCanvasSize)) {
    return false
  }

  if (otherAxisCanvasSize <= 0) {
    return false
  }

  const screenPx = axisCanvasSize * canvasZoom

  return screenPx < MIN_SHAPE_AXIS_VISUAL_SCREEN_PX
}

/**
 * Expands layout width/height (canvas units) so each axis projects to at least one screen pixel.
 * Centers growth on the shape's axis midpoint. Does not change stored geometry.
 */
export function withMinimumVisualShapeLayout(
  box: CanvasLayoutBox,
  canvasZoom: number
): CanvasLayoutBox {
  const zoom = Number.isFinite(canvasZoom) && canvasZoom > 0 ? canvasZoom : 1
  const minCanvasPx = screenPxToCanvasPx(MIN_SHAPE_AXIS_VISUAL_SCREEN_PX, zoom)

  let { x, y, width, height } = box

  if (axisNeedsMinVisualScreenSize(width, height, zoom)) {
    const targetWidth = width > 0 ? Math.max(width, minCanvasPx) : minCanvasPx

    x += width / 2 - targetWidth / 2
    width = targetWidth
  }

  if (axisNeedsMinVisualScreenSize(height, width, zoom)) {
    const targetHeight = height > 0 ? Math.max(height, minCanvasPx) : minCanvasPx

    y += height / 2 - targetHeight / 2
    height = targetHeight
  }

  return { x, y, width, height }
}
